import { type Document, INLINES } from '@contentful/rich-text-types'
import { type Asset } from 'contentful'
import { stringify } from 'flatted'
import { type GetStaticPaths, type GetStaticProps } from 'next'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import Script from 'next/script'
import { useEffect, useState } from 'react'
import PageNotFound from '~/components/shared-components/PageNotFound/PageNotFound'

import { type FooterProps } from '../components/layout/Footer/Footer'
import { type MainNavProps } from '../components/layout/MainNav/MainNav'
import { type NavigationProps } from '../components/layout/Navigation/Navigation'
import { type TopNavProps } from '../components/layout/TopNav/TopNav'
import { type EntryId } from '../components/shared-components/DocumentToReactRender/DocumentToReactRender'
import DynamicComponent from '../containers/DynamicComponent/DynamicComponent'
import { type NavBar, useNavigation } from '../context/NavigationProvider'
import {
  fetchFooter,
  fetchNavBar,
  fetchPageById,
  fetchSiteWideSettings,
} from '../hooks/useContentful'
import { type ContentfulPage, fetchPages } from '../hooks/useGraphQL'
import {
  IArticleContent,
  type IButton,
  type IFaqListingBlock,
  type INavigationBarFields,
  type INavigationElement,
  type IPage,
} from '../types/generated/contentful'
import {
  type IReducedAsset,
  type IReducedButton,
  type IReducedNavigationElement,
  type IReducedSiteNotification,
} from '../types/reducedTypes'
import { CONTENTFUL_ENVIRONMENT } from '../utils/consts'
import { getPagePathViaId } from '../utils/getPagePath'
import OrganizationSchema from './OrganizationSchema'

const PreviewPage = dynamic(
  () => import('../components/shared-components/PreviewPage/PreviewPage'),
)

const SearchResultsPage = dynamic(
  () =>
    import(
      '../components/shared-components/SearchResultsPage/SearchResultsPage'
    ),
)

type SiteWideSettings = {
  id: EntryId
  updatedAt: string
  notifications?: IReducedSiteNotification[]
}

export type SlugPageProps = {
  fullPaths: ContentfulPage[]
  page?: IPage
  navigation: NavigationProps
  topNav: TopNavProps
  mainNav: MainNavProps
  navBar: NavBar
  navItems?: string
  footer: FooterProps
  siteWideSettings?: SiteWideSettings
  fullPath: string
  isPreview?: boolean
  siteLogo?: Asset
}

export default function Page({
  page,
  fullPath,
  isPreview,
  siteLogo,
}: SlugPageProps) {
  const { findPageIdFromPath } = useNavigation()
  const [refetchedPage, setRefetchedPage] = useState<IPage | null>(null)
  const [retrying, setRetrying] = useState(false)

  const isSearchPage = fullPath === 'search-results'

  useEffect(() => {
    window?.scrollTo(0, 0)
  }, [fullPath])

  useEffect(() => {
    if (page || isSearchPage) {
      return
    }

    const retryFetchPage = async () => {
      setRetrying(true)

      try {
        const pageId = findPageIdFromPath(fullPath)

        if (!pageId) {
          return
        }

        const refetchedPageData = await fetchPageById(pageId)

        setRefetchedPage(refetchedPageData ?? null)
      } catch (error) {
        console.error('Failed to refetch the page:', error)

        setRefetchedPage(null)
      } finally {
        setRetrying(false)
      }
    }

    retryFetchPage()
  }, [fullPath, page, findPageIdFromPath, isSearchPage])

  if (isSearchPage) {
    return <SearchResultsPage />
  }

  if (retrying) {
    return <div>Loading...</div>
  }

  // Sometimes the site returns 404's, we assume due to deployments changing
  // the static assets hash, making the application unable to find the payload assets.
  // Here we try a rudimentary refetch of the page on the client side to ensure we're
  // getting the fresh payload. There must be a built-in Next.js mechanism for detecting
  // missing assets and appropriately handling, but unable to verify at this time.
  if (page || refetchedPage) {
    const pageData = page || refetchedPage
    const logo = siteLogo || refetchedPage?.fields?.siteHeaderLogo

    return (
      <>
        {logo?.fields.file.url && (
          <Head>
            <link rel="preload" href={logo.fields.file.url} as="image" />
          </Head>
        )}

        {(fullPath === '' || fullPath === 'home') && (
          <>
            <OrganizationSchema path={fullPath} />
          </>
        )}
        <>
          {pageData?.fields?.pageContent?.map((componentData, index) => (
            <div
              key={`${pageData.sys.id}-${componentData.sys.id}-${index}`}
              id={componentData.sys.id}
              className={componentData?.sys?.contentType?.sys?.id}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <DynamicComponent componentData={componentData} />
            </div>
          ))}
        </>
      </>
    )
  }

  if (isPreview && fullPath !== '404' && fullPath !== '500') {
    return <PreviewPage />
  }

  return <PageNotFound />
}

type StaticPaths = {
  params: {
    slug: string[]
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const pageRelations = await fetchPages(false)

  const paths: StaticPaths[] = []

  // Add root path for rendering the homepage
  paths.push({
    params: {
      slug: [],
    },
  })

  // Add 404 and 500 paths for pre-rendering the error pages
  paths.push(
    {
      params: {
        slug: ['404.html'],
      },
    },
    {
      params: {
        slug: ['500.html'],
      },
    },
  )

  // Add paths from GraphQL pages query
  paths.push(
    ...pageRelations.map(({ path }) => ({
      params: {
        slug: path.split('/'),
      },
    })),
  )

  paths.push({
    params: {
      slug: ['search-results'],
    },
  })

  return {
    paths,
    fallback: 'blocking', // https://github.com/netlify/next-runtime/issues/2329
  }
}

export const getStaticProps: GetStaticProps = async context => {
  const { params, preview = false } = context

  const slugArray = params?.slug ? (params?.slug as string[]) : ['home']

  const fullPath = slugArray?.join('/')

  const isSearchPage = fullPath === 'search-results'

  const fullPaths = await fetchPages(preview)

  const pageObject = fullPaths.find(({ path }) => path === fullPath)
  const pageId = pageObject?.id
  const pagePath = getPagePathViaId(pageId!, fullPaths)

  const page = pageId && (await fetchPageById(pageId!))

  function omitUndefinedValues<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T
  }

  const getNavBarFields = async () => {
    const navBar = await fetchNavBar(preview)

    return navBar[0].fields
  }

  const getFooter = async () => {
    if (!page) {
      return null
    }

    const footer = await fetchFooter(preview)
    const footerOverride = page?.fields?.footerOverride
    const defaultFooter = footer.find(footer => footer.fields.isDefaultFooter)

    return footerOverride ?? defaultFooter
  }

  const getSiteWideSettings = async () => {
    return await fetchSiteWideSettings(preview)
  }

  const [navBarFields, footerData, siteWideSettingsData] = await Promise.all([
    getNavBarFields(),
    getFooter(),
    getSiteWideSettings(),
  ])

  const formatDocument = (doc: Document): Document => {
    // @ts-ignore
    const formatData = block => {
      let data = block.data

      if (block.nodeType === INLINES.ENTRY_HYPERLINK) {
        data = {
          target: {
            sys: {
              id: data.target.sys.id,
              contentType: {
                sys: {
                  id: data.target.sys.contentType.sys.id,
                },
              },
            },
            fields: {
              title: data.target.fields.title,
              type: data.target.fields.type,
              htmlClasses: data.target.fields.htmlClasses,
              mainText: data.target.fields.mainText,
              scrollToContentReference: data.target
                .scrollToContentReference && {
                sys: {
                  id: data.target.scrollToContentReference.sys.id,
                },
              },
              redirectReference: data.target.redirectReference && {
                sys: {
                  id: data.target.redirectReference.sys.id,
                },
              },
              fileReference: data.target.fileReference && {
                fields: {
                  id: data.target.fileReference.sys.id,
                },
              },
              externalUrl: data.target.externalUrl,
              openInNewTabOverride: data.target.openInNewTabOverride,
              navigationType: data.target.navigationType,
            },
          },
        }
      }

      return data
    }

    // @ts-ignore
    const formatContent = content => {
      // @ts-ignore
      return (content ?? []).map(block => {
        if (
          block.nodeType === INLINES.EMBEDDED_ENTRY &&
          block?.data?.target?.sys?.contentType?.sys?.id ===
            'referenceValueExtractor'
        ) {
          let extractedValue =
            block?.data?.target?.fields?.contentReference?.fields[
              block?.data?.target?.fields?.referenceValueId
            ]

          if (typeof extractedValue === 'number') {
            extractedValue = extractedValue.toFixed(2)
          }

          return {
            nodeType: 'text',
            data: {},
            marks: [],
            value: extractedValue ?? 'NOT FOUND',
          }
        }

        return {
          nodeType: block.nodeType,
          data: formatData(block),
          // @ts-ignore
          marks: block.marks,
          // @ts-ignore
          value: block.value,
          content: block.content && formatContent(block.content),
        }
      })
    }

    return {
      nodeType: doc.nodeType,
      data: doc.data,
      // @ts-ignore
      content: formatContent(doc.content),
    }
  }

  const formatAsset = (asset: Asset): IReducedAsset => {
    return omitUndefinedValues({
      sys: {
        id: asset.sys.id,
      },
      fields: {
        title: asset.fields.title ?? '',
        description: asset.fields.title ?? '',
        file: {
          url: asset.fields.file.url ?? '',
          details: {
            size: asset.fields.file.details.size ?? 0,
            image: {
              height: asset.fields.file.details.image?.height ?? 0,
              width: asset.fields.file.details.image?.width ?? 0,
            },
          },
        },
      },
    })
  }

  const formatNavigationElement = (
    navigationElement: INavigationElement,
  ): IReducedNavigationElement => {
    return omitUndefinedValues<IReducedNavigationElement>({
      sys: {
        id: navigationElement.sys.id,
      },
      fields: {
        image:
          (navigationElement.fields.image &&
            formatAsset(navigationElement.fields.image)) ??
          undefined,
        redirectReference:
          (navigationElement.fields.redirectReference && {
            sys: {
              id: navigationElement.fields.redirectReference.sys.id,
            },
            fields: {
              title:
                navigationElement.fields.redirectReference.fields.title ?? '',
            },
          }) ??
          undefined,
        type: navigationElement.fields.type,
        scrollToContentReference:
          navigationElement.fields.scrollToContentReference,
        externalUrl: navigationElement.fields.externalUrl,
        htmlClasses: navigationElement.fields.htmlClasses,
        fileReference: navigationElement.fields.fileReference,
        openInNewTabOverride: navigationElement.fields.openInNewTabOverride,
        ariaLabel: navigationElement.fields.ariaLabel,
        titleAttribute: navigationElement.fields.titleAttribute,
      },
    })
  }

  const formatButton = (button: IButton): IReducedButton => {
    return {
      sys: {
        id: button.sys.id,
      },
      fields: {
        navigationType: button.fields.navigationType,
        htmlClasses: button.fields.htmlClasses,
        variant: button.fields.variant,
        text: button.fields.text,
        type: button.fields.type,
        scrollToContentReference: button.fields.scrollToContentReference && {
          sys: {
            id: button.fields.scrollToContentReference?.sys.id,
          },
        },
        redirectReference: button.fields.redirectReference && {
          sys: {
            id: button.fields.redirectReference?.sys.id,
          },
        },
        externalUrl: button.fields.externalUrl,
        openInNewTabOverride: button.fields.openInNewTabOverride,
        fileReference: button.fields.fileReference && {
          fields: {
            id: button.fields.fileReference?.fields.id,
          },
        },
        ariaLabel: button.fields.ariaLabel,
        titleAttribute: button.fields.titleAttribute,
      },
    }
  }

  const siteLogo =
    (page &&
      page?.fields.siteHeaderLogo &&
      formatAsset(page?.fields.siteHeaderLogo)) ||
    (siteWideSettingsData[0]?.fields?.siteLogo &&
      formatAsset(siteWideSettingsData[0]?.fields?.siteLogo)) ||
    null

  const navigation = Object.entries(navBarFields).reduce(
    (fields, [key, value]) => {
      if (key === 'loginText') {
        fields.loginText = value
      }

      if (key === 'loginLinks') {
        fields.loginLinks = (
          (value as INavigationBarFields['loginLinks']) ?? []
        )
          ?.filter(loginLink => loginLink.fields)
          .map(link => ({
            id: link.sys.id ?? '',
            url: link.fields.externalUrl ?? '',
            text: link.fields.mainText ?? '',
          }))
      }

      return fields
    },
    {} as NavigationProps,
  )

  navigation.logo = siteLogo
  navigation.logoUrl = (pagePath ?? '').includes('cynergy-business-finance')
    ? '/cynergy-business-finance'
    : '/'

  navigation.navItems = [
    ...(navBarFields?.topLevelPages ?? []),
    navBarFields?.cynergyBusinessFinancePage,
  ].map((page: IPage) => ({
    id: page.sys.id,
    text: page?.fields?.title ?? '',
    children: (
      page?.fields.parent?.fields?.children ??
      page?.fields.children ??
      []
    ).map((child: IPage) => ({
      id: child.sys.id,
      text: child?.fields?.title ?? '',
      groups: (child?.fields?.navigationMenu?.fields.navList ?? []).map(
        navList => ({
          id: navList.sys.id,
          title: navList.fields.title ?? '',
          links: (navList.fields.pageLinkReferences ?? []).map(navItem => ({
            id: navItem.sys.id,
            text: navItem.fields.title ?? '',
          })),
        }),
      ),
    })),
  }))

  const footer: FooterProps = omitUndefinedValues<FooterProps>({
    id: footerData?.sys.id ?? '',
    appText: footerData?.fields.appText ?? '',
    appImages: (footerData?.fields.appImages ?? []).map(
      formatNavigationElement,
    ),
    socialMediaText: footerData?.fields.socialMediaText ?? '',
    socialMediaImages: (footerData?.fields.socialMediaImages ?? []).map(
      formatNavigationElement,
    ),
    lists: (footerData?.fields.footerMenuLists ?? []).map(list => ({
      id: list.sys.id,
      title: list.fields.title ?? '',
      pages: (list.fields.pageLinkReferences ?? [])
        .map(page => ({
          id: page.sys.id,
          text: page.fields.title ?? '',
        }))
        .filter(({ id }) => id && getPagePathViaId(id, fullPaths)),
      externalLinks: (list.fields.additionalLinks ?? []).map(link => ({
        id: link.sys.id,
        text: link.fields.mainText ?? '',
        url: link.fields.externalUrl ?? '/',
      })),
    })),
    additionalButton:
      (footerData?.fields.additionalButton &&
        formatButton(footerData?.fields.additionalButton)) ??
      undefined,
    legalText:
      footerData?.fields.legalText &&
      formatDocument(footerData?.fields.legalText),
    awardImages: (footerData?.fields.awardImages ?? []).map(formatAsset),
    extraText:
      footerData?.fields.extraText &&
      formatDocument(footerData?.fields.extraText),
  })

  const siteWideSettings = omitUndefinedValues<SiteWideSettings>({
    id: siteWideSettingsData[0].sys.id,
    updatedAt: siteWideSettingsData[0].sys.updatedAt,
    notifications:
      siteWideSettingsData[0].fields.notifications &&
      siteWideSettingsData[0].fields.notifications
        .filter(n => n.fields)
        .map(n => {
          return {
            sys: { id: n.sys.id, updatedAt: n.sys.updatedAt },
            fields: {
              notificationBody: formatDocument(n.fields.notificationBody),
              notificationType: n.fields.notificationType,
            },
          }
        }),
  })

  const formatPage = (page?: IPage) => {
    if (!page) {
      return null
    }

    return stringify(page, (key, value) => {
      if (value?.sys?.contentType?.sys?.id === 'page') {
        if (key === '') {
          return {
            sys: {
              id: value.sys.id,
            },
            fields: value.fields,
          }
        }

        if (key === 'parent' || key === 'redirectReference') {
          return {
            sys: {
              id: value.sys.id,
            },
            fields: {
              title: value.fields.title,
            },
          }
        }
      }

      if (value?.sys?.contentType?.sys?.id === 'newsAndArticlesPreview') {
        return {
          sys: {
            id: value?.sys?.id,
            contentType: value?.sys?.contentType?.sys?.id && {
              sys: {
                id: value.sys.contentType.sys.id,
              },
            },
          },
          fields: {
            title: value?.fields?.title,
            button: value?.fields?.button,
            articles: (value?.fields.articles || []).map(
              (article: IArticleContent) => ({
                fields: {
                  parentpage: article?.fields?.parentpage && {
                    sys: {
                      id: article.fields.parentpage.sys.id,
                    },
                  },
                  dateOfArticle: article?.fields?.dateOfArticle,
                  articleName: article?.fields?.articleName,
                  articlePreviewImage: article?.fields?.articlePreviewImage,
                  displayOnMainSite: article?.fields?.displayOnMainSite,
                  displayOnBusinessFinance:
                    article?.fields?.displayOnBusinessFinance,
                  articlePreviewBackgroundColour:
                    article?.fields?.articlePreviewBackgroundColour?.value,
                  articlePreviewTextColour:
                    article?.fields?.articlePreviewTextColour?.value,
                },
              }),
            ),
          },
        }
      }

      if (key === 'children') {
        return (value ?? []).map((child: IPage) => ({
          sys: {
            id: child?.sys?.id,
          },
          fields: {
            title: child?.fields?.title,
          },
        }))
      }

      if (
        key === 'metadata' ||
        key === 'contentfulNameReference' ||
        key === 'contentfulReferenceName' ||
        key === 'footerOverride'
      ) {
        return undefined
      }

      if (value?.nodeType === 'document') {
        return formatDocument(value)
      }

      if (key === 'sys') {
        return {
          id: value.id,
          updatedAt: value.updatedAt,
          contentType: value.contentType?.sys?.id && {
            sys: {
              id: value.contentType.sys.id,
            },
          },
        }
      }

      if (key === 'navigationMenu') {
        return undefined
      }

      if (key === 'parentpage') {
        return {
          sys: {
            id: value?.sys?.id,
          },
        }
      }

      if (key === 'faqListingBlocks') {
        return (value || []).map((faqListingBlock: IFaqListingBlock) => ({
          sys: {
            id: faqListingBlock.sys.id,
          },
          fields: {
            title: faqListingBlock.fields.title,
            icon: formatAsset(faqListingBlock.fields.icon),
            listItems: (faqListingBlock.fields.listItems ?? []).map(
              (listItem: IPage) => ({
                sys: {
                  id: listItem.sys.id,
                },
                fields: {
                  title: listItem.fields.title,
                  slug: listItem.fields.slug,
                },
              }),
            ),
          },
        }))
      }

      if (value?.sys?.contentType?.sys?.id) {
        return {
          sys: {
            id: value.sys.id,
            contentType: {
              sys: {
                id: value.sys.contentType.sys.id,
              },
            },
          },
          fields: value.fields,
        }
      }

      return value
    })
  }

  return {
    props: {
      // Provide all available paths to the frontend
      fullPaths,
      page: (page && formatPage(page)) || null,
      // Navigation
      navigation,
      // Footer
      footer,
      siteWideSettings,
      fullPath,
      isPreview: preview || false,
      siteLogo,
      contentfulEnv: CONTENTFUL_ENVIRONMENT,
    },
    notFound: !page && !preview && !isSearchPage,
  }
}
